import { FC, useEffect, useMemo } from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { Box, Grid, Typography } from '@mui/material';
import { InitialValues } from '../schema';
import NewBorrowerFormFieldset from '../NewBorrowerFormFieldset';
import { CustomTooltip } from '../../../../CustomTooltip';
import SelectField from '../../../../hook_form_fields/SelectField';
import NumberField from '../../../../hook_form_fields/NumberField';
import InputField from '../../../../hook_form_fields/InputField';
import SelectableCardGroup from '../../../../SelectableCardGroup';
import { InfoIcon } from '../../../../loan_application/icons';
import { PLACEHOLDERS } from '../../../../../constants';
import { currencyFormatter } from '../../../../../utils/formatters';
import WarningMessage from '../../../../../pages/dashboard/loan_detail/flows/NewWelcomeFlow/components/WarningMessage';

type NonSolarKey = 'energyStorage' | 'evCharging' | 'roofRepair' | 'other';

const NON_SOLAR_OPTIONS: ReadonlyArray<{ key: NonSolarKey; label: string }> = [
  { key: 'energyStorage', label: 'Energy storage' },
  { key: 'evCharging', label: 'EV charging' },
  { key: 'roofRepair', label: 'Roof repairs/replacement' },
  { key: 'other', label: 'Other' },
];

const AMOUNT_PATH: Record<
  NonSolarKey,
  | 'application.energy_storage_amount'
  | 'application.ev_charging_amount'
  | 'application.roof_repair_amount'
  | 'application.other_non_solar_amount'
> = {
  energyStorage: 'application.energy_storage_amount',
  evCharging: 'application.ev_charging_amount',
  roofRepair: 'application.roof_repair_amount',
  other: 'application.other_non_solar_amount',
};

const BATTERY_EXCEEDED_WARNING =
  'For loan terms over 10 years (including 10/20), the battery cost must be less than 50% of the total cost of the project. Please note your application is unlikely to be approved if the term is not decreased or the battery cost is not decreased.';
const ROOF_EXCEEDED_WARNING =
  'Roof costs cannot exceed 50% of the total project cost. Please note that your application is unlikely to be approved unless you decrease the roof portion of this loan.';
const EV_CHARGING_EXCEEDED_WARNING =
  'EV Charging costs cannot exceed 50% of the total project cost. Please note that your application is unlikely to be approved unless you decrease the EV charging portion of this loan.';

const nonSolarChoices = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

const setValueOptions = {
  shouldValidate: true,
  shouldDirty: true,
  shouldTouch: true,
};

const NonSolarProjectComponents: FC<{
  control: Control<InitialValues>;
  setValue: UseFormSetValue<InitialValues>;
}> = ({ control, setValue }) => {
  const nonSolarValues = useWatch({ control, name: 'nonSolar' });
  const [net_funding, loanTerm] = useWatch({
    control,
    name: ['application.net_funding', 'loanTerm'],
  });
  const energyStorageAmount = useWatch({
    control,
    name: 'application.energy_storage_amount',
  });
  const evChargingAmount = useWatch({
    control,
    name: 'application.ev_charging_amount',
  });
  const roofRepairAmount = useWatch({
    control,
    name: 'application.roof_repair_amount',
  });
  const otherNonSolarAmount = useWatch({
    control,
    name: 'application.other_non_solar_amount',
  });

  const amountByKey: Record<NonSolarKey, unknown> = {
    energyStorage: energyStorageAmount,
    evCharging: evChargingAmount,
    roofRepair: roofRepairAmount,
    other: otherNonSolarAmount,
  };

  const nonSolarTotal = useMemo(
    () =>
      Number(energyStorageAmount || 0) +
      Number(evChargingAmount || 0) +
      Number(roofRepairAmount || 0) +
      Number(otherNonSolarAmount || 0),
    [
      energyStorageAmount,
      evChargingAmount,
      roofRepairAmount,
      otherNonSolarAmount,
    ]
  );

  const roofExceeded = useMemo(() => {
    if (!net_funding || !roofRepairAmount || Number(roofRepairAmount) <= 0)
      return false;
    return Number(roofRepairAmount) > Number(net_funding) * 0.5;
  }, [net_funding, roofRepairAmount]);

  const evChargingExceeded = useMemo(() => {
    if (!net_funding || !evChargingAmount || Number(evChargingAmount) <= 0)
      return false;
    return Number(evChargingAmount) > Number(net_funding) * 0.5;
  }, [net_funding, evChargingAmount]);

  const batteryExceeded = useMemo(() => {
    if (
      !net_funding ||
      !loanTerm ||
      !energyStorageAmount ||
      Number(energyStorageAmount) <= 0
    )
      return false;
    const termsForExceeding = ['1020', '1515', '2020'];
    return (
      termsForExceeding.includes(loanTerm) &&
      Number(energyStorageAmount) > Number(net_funding) * 0.5
    );
  }, [net_funding, loanTerm, energyStorageAmount]);

  const warningFor: Partial<Record<NonSolarKey, string>> = {};
  if (batteryExceeded) warningFor.energyStorage = BATTERY_EXCEEDED_WARNING;
  if (evChargingExceeded) warningFor.evCharging = EV_CHARGING_EXCEEDED_WARNING;
  if (roofExceeded) warningFor.roofRepair = ROOF_EXCEEDED_WARNING;

  useEffect(() => {
    setValue('nonSolar.nonSolarAmount', nonSolarTotal, setValueOptions);
  }, [nonSolarTotal, setValue]);

  useEffect(() => {
    if (!nonSolarValues?.hasNonSolarComponent) {
      setValue('nonSolar.energyStorage', false, setValueOptions);
      setValue('nonSolar.evCharging', false, setValueOptions);
      setValue('nonSolar.roofRepair', false, setValueOptions);
      setValue('nonSolar.other', false, setValueOptions);
    }
  }, [nonSolarValues?.hasNonSolarComponent]);

  useEffect(() => {
    if (!nonSolarValues?.energyStorage) {
      setValue('application.energy_storage_amount', '');
    }
    if (!nonSolarValues?.evCharging) {
      setValue('application.ev_charging_amount', '');
    }
    if (!nonSolarValues?.roofRepair) {
      setValue('application.roof_repair_amount', '');
    }
    if (!nonSolarValues?.other) {
      setValue('nonSolar.otherValue', '');
      setValue('application.other_non_solar_amount', '');
    }
  }, [
    nonSolarValues?.energyStorage,
    nonSolarValues?.evCharging,
    nonSolarValues?.roofRepair,
    nonSolarValues?.other,
  ]);

  return (
    <NewBorrowerFormFieldset>
      <Grid
        container
        maxWidth="md"
        spacing={2}
        title="Non-Solar Project Components"
      >
        <Grid item xs={12} sm={12}>
          <Typography variant="body2" mb={1}>
            Does a portion of the requested loan amount fund one or more of the
            following: energy storage, EV charging, roof repairs/replacement, or
            other non-solar property enhancements?
            <CustomTooltip title="Underwriting may require additional information about these improvements">
              <InfoIcon
                sx={{
                  fontSize: '.75rem',
                  marginTop: '.3125rem',
                  marginLeft: '.25rem',
                  verticalAlign: 'center',
                }}
              />
            </CustomTooltip>
            <Typography variant="body2" color="error" component="span">
              {' '}
              *
            </Typography>
          </Typography>
          <SelectField
            fullWidth
            control={control}
            name="nonSolar.hasNonSolarComponent"
            required
            choices={nonSolarChoices}
          />
        </Grid>

        {nonSolarValues?.hasNonSolarComponent && (
          <Grid item xs={12} sm={12}>
            <Typography variant="body2" mb={1}>
              Which of the following improvements will be funded by the loan?
              <CustomTooltip title="Underwriting may require additional information about these improvements">
                <InfoIcon
                  sx={{
                    fontSize: '.75rem',
                    marginTop: '.3125rem',
                    marginLeft: '.25rem',
                    verticalAlign: 'center',
                  }}
                />
              </CustomTooltip>
              <Typography variant="body2" color="error" component="span">
                {' '}
                *
              </Typography>
            </Typography>
            <Typography variant="body2" sx={{ color: '#868795' }} mb={1}>
              Please enter the estimated cost of each improvement if you have
              it.
            </Typography>

            <Controller
              control={control}
              name="nonSolarChecked"
              render={({ fieldState, formState }) => {
                const { error } = fieldState;
                const { touchedFields, isSubmitted } = formState;
                const nonSolarTouched = touchedFields['nonSolar'];
                const anyNonSolarCheckboxTouched =
                  nonSolarTouched?.['energyStorage'] ||
                  nonSolarTouched?.['evCharging'] ||
                  nonSolarTouched?.['roofRepair'] ||
                  nonSolarTouched?.['other'];
                const shouldShow =
                  !!error && (anyNonSolarCheckboxTouched || isSubmitted);
                return shouldShow ? (
                  <Typography
                    variant="body2"
                    color="error"
                    component="div"
                    mb={1}
                  >
                    {error.message}
                  </Typography>
                ) : (
                  <></>
                );
              }}
            />

            <SelectableCardGroup
              columns={2}
              options={NON_SOLAR_OPTIONS}
              isSelected={(key) => !!nonSolarValues?.[key]}
              onToggle={(key) =>
                setValue(
                  `nonSolar.${key}` as const,
                  !nonSolarValues?.[key],
                  setValueOptions
                )
              }
              renderExpanded={(opt) => {
                const amount = amountByKey[opt.key];
                const showPercent =
                  !!net_funding && !!amount && Number(amount) > 0;
                const rawPercent = showPercent
                  ? (Number(amount) / Number(net_funding)) * 100
                  : 0;
                const percentLabel =
                  rawPercent > 0 && rawPercent < 1
                    ? '<1%'
                    : `${Math.round(rawPercent)}%`;
                const warning = warningFor[opt.key];
                return (
                  <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
                  >
                    {opt.key === 'other' && (
                      <InputField
                        control={control}
                        name="nonSolar.otherValue"
                        placeholder="Describe the improvement"
                        fullWidth
                        multiline
                        minRows={1}
                        maxRows={4}
                        maxLength={140}
                        touched
                      />
                    )}
                    <NumberField
                      control={control}
                      name={AMOUNT_PATH[opt.key]}
                      thousandSeparator
                      startAdornmentText="$"
                      placeholder={
                        nonSolarTotal ? PLACEHOLDERS.OPTIONAL : 'Amount'
                      }
                      fullWidth
                      allowNegative={false}
                      decimalScale={2}
                      showError={false}
                    />
                    {showPercent && (
                      <Typography
                        variant="caption"
                        sx={{ display: 'block', color: '#868795' }}
                      >
                        {percentLabel} of net funding
                      </Typography>
                    )}
                    {warning && (
                      <WarningMessage
                        warningMessage={warning}
                        sx={{ mt: 0, ml: 0 }}
                        boxSx={{ mt: 0 }}
                      />
                    )}
                  </Box>
                );
              }}
            />

            <Box mt={2}>
              <Typography variant="body2">
                Estimated total cost of the non-solar project components
              </Typography>
              <Typography variant="h6">
                {currencyFormatter.format(nonSolarTotal)}
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </NewBorrowerFormFieldset>
  );
};

export default NonSolarProjectComponents;
